/* eslint-disable import/exports-last */
import classNames from 'classnames'
import styles from './AnimateNumber.module.css'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

export type AnimateNumberProps = {
  children: string | number
  className?: string
  style?: CSSProperties
  animationDurationInMs?: number
  maxNumberScale?: number
}

export function processNumber(num: string | number) {
  const num_ = typeof num === 'number' ? num.toString() : num
  let prefix = ''
  let suffix = ''
  const separators: string[] = []
  const integers: string[] = []
  let currentSegment: string = ''
  let prevCharIsNumber = false
  const isNumber = (char: string | undefined) => typeof char === 'string' && /[0-9]/.test(char)
  for (let i = 0; i <= num_.length; i++) {
    const char = num_[i]
    const charIsNumber = isNumber(char)

    // Handle transition from non-number to number
    if (charIsNumber && !prevCharIsNumber) {
      if (!integers.length) {
        prefix = currentSegment
      } else if (currentSegment) {
        separators.push(currentSegment)
      }
      currentSegment = ''
    }

    // Handle transition from number to non-number
    if (!charIsNumber && prevCharIsNumber) {
      integers.push(currentSegment)
      currentSegment = ''
    }

    // Handle continuing non-number
    if (!charIsNumber && !prevCharIsNumber) {
      suffix = currentSegment
    }

    prevCharIsNumber = charIsNumber
    currentSegment += char
  }
  return {
    prefix,
    suffix,
    separators,
    integers,
  }
}

function NumberSlot({ num, separator }: { num: string; separator: string | undefined }) {
  const numStr = parseInt(num, 10).toString()
  const zeroPadLength = Math.max(num.length - numStr.length, 0)
  return (
    <span
      data-zero-pad={'0'.repeat(zeroPadLength)}
      data-separator={separator ?? ''}
      style={
        {
          '--number': num,
        } as CSSProperties
      }
    ></span>
  )
}

function AnimateNumber({
  children: num,
  style,
  className,
  animationDurationInMs = 1000,
  maxNumberScale = 2,
}: AnimateNumberProps) {
  const prevValue = useRef<number | null>(null)
  const [delta, setDelta] = useState(0)
  const res = useMemo(() => processNumber(num), [num])
  const numberValue = useMemo(() => {
    const lastIndex = res.integers.length - 1
    const value = parseFloat(
      res.integers.slice(0, lastIndex).join('') +
        (res.separators[lastIndex - 1] ? '.' + res.integers[lastIndex] : res.integers[lastIndex])
    )
    return value
  }, [res])

  useEffect(() => {
    if (isNaN(numberValue)) return
    const prev = prevValue.current ?? 0
    const delta = numberValue - prev
    prevValue.current = numberValue
    if (prevValue.current === null) {
      return setDelta(0)
    }
    setDelta(delta)
    const timeout = setTimeout(() => {
      setDelta(0)
    }, animationDurationInMs)
    return () => clearTimeout(timeout)
  }, [animationDurationInMs, numberValue])

  const scale = useMemo(
    () => Math.max(Math.max(maxNumberScale - 1, 0) * ((Math.log10(delta / 100) + 3) / 4) + 1, 1),
    [delta, maxNumberScale]
  )

  if (isNaN(numberValue)) return null
  const { prefix, suffix, separators, integers } = res
  return (
    <div
      className={classNames(styles.number, className)}
      data-prefix={prefix}
      data-suffix={suffix}
      style={
        {
          ...style,
          '--number-scale': scale,
          '--number-animation-duration': `${animationDurationInMs}ms`,
        } as CSSProperties
      }
    >
      {integers.map((num, index) => {
        const separator = separators[index]
        const key = integers.length - 1 - index
        return <NumberSlot key={key} num={num} separator={separator} />
      })}

      <span key="sr-only" className="sr-only">
        {num}
      </span>
    </div>
  )
}

export default AnimateNumber
